import { Box, Button, Card, IconButton, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../../service/api'
import TableHeader from '../../common/TableHeader'
import { formatDate } from '../../../utils/utilities'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import { OpenInNew } from '@mui/icons-material'
import noAssignmentImg from '../../../assets/noassignment.svg'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import CustomTable from '../../common/custom-table/CustomTable'
import { useUserStore } from '../../../store/user.store'
import AuditPracticeSessionViewer from './AuditPracticeSessionViewer'




const AuditPracticeSessionsList = () => {


    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByAssignmentCount: "1" } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [error, setError] = useState( null )
    const [practiceSessions, setPracticeSessions] = useState( [] )
    const [loading, setLoading] = useState( true )
    const [selectedPracticeSession, setSelectedPracticeSession] = useState( null )

    const UserStore = useUserStore()

    const { layout } = useTheme()

    const navigate = useNavigate()

    const columns = useMemo( () => [
        { id: 2, name: "Title", field: "title", width: "220px", padding: "10px 20px" },
        { id: 3, name: 'Faculty name', field: "faculty", renderCell: row => row.creator_info.name?.toLowerCase(), cellStyles: { textTransform: "capitalize" }, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.creator_info.empcode}?legacy=false`, width: "150px", align: "left", padding: "10px 20px" },
        { id: 6, name: 'Institute', field: "institute", renderCell: row => row?.creator_info?.institute, padding: "10px 20px" },
        { id: 9, name: 'Department', field: "department", renderCell: row => row?.creator_info?.department_name, padding: "10px 20px" },
        { id: 4, name: "Subject", field: "subject", renderCell: row => row.subject?.subject_name || "Not available", adding: "10px 20px" },
        { id: 5, align: "center", field: "created_at", renderCell: row => formatDate( row.created_at ), name: "Created on", width: "150px", padding: "10px 20px" },
        { id: 7, align: "center", field: "topics", name: "Topics", renderCell: row => Object.values( row.modules_and_topics || {} ).reduce( ( acc, item ) => acc + ( item.topics?.length || 0 ), 0 ), padding: "10px 20px" },
        {
            id: 8, align: "right", field: "actions", name: "Actions", padding: "10px 20px", renderCell: row => (
                <Box display="flex" justifyContent="flex-end" gap="10px" alignItems="center">
                    <Tooltip title="Open details">
                        <IconButton onClick={() => setSelectedPracticeSession( row )} color="primary" size="small"><OpenInNew fontSize='small' /></IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ], [] )

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'selectedInstitute', "all" )
            prev.set( 'selectedDepartment', "all" )
            return prev
        } )
    }

    const handleDialogClose = ( refresh = false ) => {
        setSelectedPracticeSession( null )
        if ( refresh )
            getData()
    }


    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getPracticeSessionsForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setPracticeSessions( data )
        } catch ( err ) {
            console.log( err )
            setError( err?.message?.data?.message || "Something went wrong while fetching practice sessions." )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute] )

    useEffect( () => {
        getData()
    }, [getData] )

    const filtered = useMemo( () => {
        if ( selectedDepartment === "all" || !selectedDepartment )
            return practiceSessions

        const departemtnId = +selectedDepartment?.split( '-' ).at( 0 )
        return practiceSessions.filter( ps => +ps.creator_info?.department_id === departemtnId )
    }, [selectedDepartment, practiceSessions] )


    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {selectedPracticeSession && <AuditPracticeSessionViewer practiceSessionId={selectedPracticeSession._id} refreshList={getData} onClose={() => setSelectedPracticeSession( null )} open={Boolean( selectedPracticeSession )} />}

            <Box display="flex" gap="20px" alignItems="flex-end" justifyContent="space-between" >
                <Typography variant="h1" fontWeight="600" component="h2" fontSize="14px">Practice Sessions</Typography>
                <Button size="small" onClick={() => navigate( `/${["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? "admin" : "audit"}/practice-with-simba` )} variant="contained" color='secondary' disableElevation sx={{ height: "33.5px", textTransform: "capitalize", }} >View Stats</Button>
            </Box >

            {!loading && filtered.length > 0 && <CustomTable columns={columns} globalSearch="Search practice sessions" data={filtered} />}
            {!loading && filtered.length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <img src={noAssignmentImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No practice sessions were found for applied filters!</Typography>
                <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>
            </Box>}
            {error && <TryAgainMessageBlock getData={getData} err={error} loading={loading} />}
            {loading && <DataLoadingSpinner waitingMessage="Loading practice sessions please wait...." />}
        </Box>
    )
}

export default AuditPracticeSessionsList

/*

<Box overflow="hidden" display="flex" flexDirection="column" justifyContent="space-between" borderRadius={layout.borderRadius} border={border[1]}>
                <TableContainer sx={{ flexGrow: 1, overflow: "auto", maxHeight: "500px" }} className='custom-scrollbar' >
                    <Table stickyHeader>
                        <TableHeader columns={columns} />
                        <TableBody>
                            {practiceSessions.filter( filterFunction ).slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( ( row, index ) => {
                                return <TableRow className='table-row' key={row.id}>
                                    <TableCell sx={{ padding: columns[0].padding || "10px 20px", color: table.color, textTransform: "capitalize", fontSize: "12px" }}>{row.title}</TableCell>
                                    <TableCell align={columns[1].align || "left"} sx={{ fontSize: "12px", padding: columns[1].padding || "10px 20px", textTransform: "capitalize", color: table.color }}><Typography maxWidth="100px" fontSize="12px" sx={{ '&:hover': { textDecoration: "underline" } }}><Link to={`/${window.location.pathname?.includes('admin') ? "admin" : "audit"}/faculty/${row.creator_info.empcode}?legacy=false`} className='default-link' >{row.creator_info.name?.toLowerCase()}</Link></Typography></TableCell>
                                    <TableCell align={columns[2].align || "left"} sx={{ fontSize: "12px", padding: columns[2].padding || "10px 20px", textTransform: "capitalize", color: table.color, }}>{row?.creator_info?.department_name?.toLowerCase()}</TableCell>
                                    <TableCell align={columns[3].align || "left"} sx={{ fontSize: "12px", padding: columns[3].padding || "10px 20px", color: table.color }}>{row.subject?.subject_name || "Not available"}</TableCell>
                                    <TableCell align={columns[4].align || "left"} sx={{ fontSize: "12px", padding: columns[4].padding || "10px 20px", color: table.color }}>{formatDate( row.created_at )}</TableCell>
                                    <TableCell align={columns[5].align || "left"} sx={{ fontSize: "12px", padding: columns[5].padding || "10px 20px", color: table.color }}>{Object.values( row.modules_and_topics || {} ).reduce( ( acc, item ) => acc + ( item.topics?.length || 0 ), 0 )}</TableCell>
                                    <TableCell align={columns[6].align || "left"} sx={{ fontSize: "12px", padding: columns[6].padding || "10px 20px", color: table.color }}>
                                        <Box display="flex" justifyContent="flex-end" gap="10px" alignItems="center">
                                            <Tooltip title="Open details">
                                                <IconButton onClick={() => setSelectedPracticeSession( row )} color="primary" size="small"><OpenInNew fontSize='small' /></IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            } )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    sx={{ position: "sticky", bottom: 0, flexShrink: 0 }}
                    rowsPerPageOptions={[50, 100, 150, 200]}
                    component="div"
                    count={practiceSessions.filter( filterFunction ).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={( event, val ) => setPage( val )}
                    onRowsPerPageChange={( event ) => {
                        setRowsPerPage( parseInt( event.target.value, 10 ) )
                        localStorage.setItem( 'adminAuditRowsPerPage', event.target.value )
                        setPage( 0 )
                    }}
                />
            </Box>

*/
